import React, { useState, useRef, useLayoutEffect } from 'react';
import { observer } from 'mobx-react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';
import { listViewsStore } from './ListViewController';
import { deferToNextFrame } from './viewStateUtils';
import { dialogStore } from 'components/layout/modals/DialogStore';
import EllipsisTooltipLabel from 'ats/components/ellipsis-tooltip-label/EllipsisTooltipLabel';

const DELETE_VIEW_DIALOG = {
	title: 'Delete view',
	message: 'This saved view will be permanently deleted. Are you sure?',
	confirmLabel: 'Delete',
	confirmButtonClass: 'is-danger'
};

function ViewsDropdown({
	section,
	controller,
	onViewStateChange,
	viewStateKey,
	isModified = false
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [isNaming, setIsNaming] = useState(false);
	const [newViewName, setNewViewName] = useState('');
	const [saveError, setSaveError] = useState(null);
	const dropdownRef = useRef(null);
	const inputRef = useRef(null);
	const [menuWidth, setMenuWidth] = useState(undefined);

	const views = listViewsStore.getViews(section);
	const activeView = listViewsStore.getActiveView(section);
	const activeLabel = activeView?.name || 'View';
	const isLoading = listViewsStore.loadingSection === section;
	const shouldHide = !isLoading && views.length === 0;

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: open => {
			setIsOpen(open);
			if (!open) {
				setIsNaming(false);
				setNewViewName('');
				setSaveError(null);
			}
		},
		middleware: [offset(2), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start'
	});

	const click = useClick(context, { preventScroll: true });
	const dismiss = useDismiss(context);
	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	useLayoutEffect(() => {
		if (isOpen && dropdownRef.current) {
			setMenuWidth(dropdownRef.current.offsetWidth);
		}
	}, [isOpen, isNaming]);

	useLayoutEffect(() => {
		if (isNaming && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isNaming]);

	const handleSelectView = async view => {
		if (view.id === activeView?.id) {
			setIsOpen(false);
			return;
		}
		setIsOpen(false);
		await deferToNextFrame();
		await controller.selectView(view);
		onViewStateChange?.();
	};

	const handleSaveChanges = async () => {
		setSaveError(null);
		const result = await controller.saveChanges();
		if (result?.status === 'fail') {
			setSaveError(result.message || 'Unable to save view');
			return;
		}
		onViewStateChange?.();
		setIsOpen(false);
	};

	const handleSaveAsNew = async event => {
		event.preventDefault();
		const trimmed = newViewName.trim();
		if (!trimmed) {
			setSaveError('Enter a view name');
			return;
		}
		setSaveError(null);
		const result = await controller.saveAsNew(trimmed);
		if (result?.status === 'fail') {
			setSaveError(result.message || 'Unable to save view');
			return;
		}
		onViewStateChange?.();
		setIsOpen(false);
		setIsNaming(false);
		setNewViewName('');
	};

	const handleDeleteView = view => {
		setSaveError(null);
		setIsOpen(false);
		dialogStore.openDialog(DELETE_VIEW_DIALOG, async () => {
			await deferToNextFrame();
			const result = await controller.deleteView(view);
			if (result?.status === 'fail') {
				setSaveError(result.message || 'Unable to delete view');
				return;
			}
			onViewStateChange?.();
		});
	};

	const canDeleteView = view => !view.isSystem && !view.systemKey;

	const canSaveChanges =
		isModified && activeView && activeView.systemKey !== 'default';

	if (shouldHide) {
		return null;
	}

	return (
		<div
			className={`views-dropdown dropdown-ats ${isModified ? 'is-modified' : ''}`}
			ref={dropdownRef}>
			<button
				ref={refs.setReference}
				type="button"
				className="views-dropdown-btn dropdown-ats-btn"
				{...getReferenceProps()}>
				<img
					className="views-dropdown-icon"
					src="/ats/icons/bookmark-icon.svg"
					alt=""
				/>
				<span className="views-dropdown-label-wrap">
					<EllipsisTooltipLabel
						wrapperClassName="views-dropdown-label-tooltip"
						className="dropdown-ats-btn-label views-dropdown-label"
						text={activeLabel}
					/>
					{isModified ? (
						<span className="views-dropdown-modified-mark">*</span>
					) : null}
				</span>
				<svg className="chevron-down-icon" viewBox="0 0 22 22">
					<path
						d="M6 9l5 5 5-5"
						stroke="currentColor"
						strokeWidth="1"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill="none"
					/>
				</svg>
			</button>

			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<ul
							ref={refs.setFloating}
							style={{ ...floatingStyles, minWidth: menuWidth || 200 }}
							{...getFloatingProps()}
							className="dropdown-ats-menu views-dropdown-menu">
							{views.map(view => (
								<li
									key={view.id}
									className={`views-dropdown-view-item${
										view.id === activeView?.id ? ' is-active' : ''
									}`}>
									<button
										type="button"
										className="views-dropdown-view-select"
										onClick={() => handleSelectView(view)}>
										<span className="views-dropdown-view-name">
											{view.name}
										</span>
										{view.id === activeView?.id ? (
											<img
												className="views-dropdown-check-icon"
												src="/ats/icons/check-views-icon.svg"
												alt=""
											/>
										) : null}
									</button>
									{canDeleteView(view) ? (
										<button
											type="button"
											className="views-dropdown-delete-btn"
											aria-label={`Delete ${view.name}`}
											onClick={event => {
												event.stopPropagation();
												handleDeleteView(view);
											}}>
											<img src="/ats/icons/trash-icon.svg" alt="" />
										</button>
									) : null}
								</li>
							))}
							<li className="views-dropdown-divider" aria-hidden="true" />
							{isNaming ? (
								<li className="views-dropdown-name-form">
									<form onSubmit={handleSaveAsNew}>
										<input
											ref={inputRef}
											type="text"
											value={newViewName}
											placeholder="View name"
											maxLength={80}
											onChange={e => setNewViewName(e.target.value)}
										/>
										<div className="views-dropdown-name-actions">
											<button type="submit">Save</button>
											<button
												type="button"
												onClick={() => {
													setIsNaming(false);
													setNewViewName('');
													setSaveError(null);
												}}>
												Cancel
											</button>
										</div>
									</form>
								</li>
							) : (
								<li
									onClick={() => {
										setIsNaming(true);
										setSaveError(null);
									}}>
									Save as new view…
								</li>
							)}
							<li
								className={canSaveChanges ? '' : 'is-disabled'}
								onClick={() => {
									if (canSaveChanges) handleSaveChanges();
								}}>
								Save changes to current view
							</li>
							{saveError ? (
								<li className="views-dropdown-error">{saveError}</li>
							) : null}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
}

export default observer(ViewsDropdown);
